import { createSelector } from 'reselect';
import { ROOT_SLICE_LINES_STATE } from '../constants';
import { selectItems, selectServerItems } from '../main/selectors';
import { computeUserCompositeId } from '../main/transform';

export const selectSliceState = (state = {}) =>
	state[ROOT_SLICE_LINES_STATE] || state;

export const selectSelectedCustomers = state => {
  const slice = selectSliceState(state);
	const { selectedCustomers } = slice;
	return selectedCustomers;
};

export const selectSelectedOwners = state => {
  const slice = selectSliceState(state);
	const { selectedOwners } = slice;
	return selectedOwners;
};

export const selectSelectedEditors = state => {
  const slice = selectSliceState(state);
	const { selectedEditors } = slice;
	return selectedEditors;
};

export const selectSelectedLineNames = state => {
  const slice = selectSliceState(state);
	const { selectedLineNames } = slice;
	return selectedLineNames;
};

export const selectSelectedSurveyTypes = state => {
  const slice = selectSliceState(state);
	const { selectedSurveyTypes } = slice;
	return selectedSurveyTypes;
};

// ***********
// LISTS - CUSTOMERS
// ***********

export const selectCustomers = createSelector(
	selectItems, 
	(items) => {
		const customers = new Set([]);
		items.forEach(item => {
			if (item?.customer?.name) {
				customers.add(item.customer.name);
			}
		});
		return [...customers].sort();
	}
);

export const selectSelectedCustomersList = createSelector(
	selectCustomers,
	selectSelectedCustomers,
	(customers, selectedCustomers) => {
		return customers.filter(c => selectedCustomers[c]);
	}
)

// ***********
// LISTS - OWNERS
// ***********

export const selectOwners = createSelector(
	selectItems, 
	(items) => {
		const ownersMap = items.reduce((acc, item) => {
			if (item?.owner?.compositeId && !acc[item?.compositeId]) {
				acc[item.owner.compositeId] = item.owner;
			}
			return acc;
		}, {});
		const owners = Object.keys(ownersMap).map(compositeId => ownersMap[compositeId])
		return owners;
	}
);

export const selectSelectedOwnersList = createSelector(
	selectOwners,
	selectSelectedOwners,
	(owners, selectedOwners) => {
		return owners.filter(u => u?.compositeId && selectedOwners[u.compositeId]);
	}
)

// ***********
// LISTS - EDITORS
// ***********

export const selectEditors = createSelector(
	selectItems, 
	(items) => {
		const editorsMap = items.reduce((acc, item) => {
			if (item?.editedBy?.compositeId && !acc[item?.compositeId]) {
				acc[item.editedBy.compositeId] = item.editedBy;
			}
			return acc;
		}, {});
		const editors = Object.keys(editorsMap).map(compositeId => editorsMap[compositeId])
		return editors;
	}
);

export const selectSelectedEditorsList = createSelector(
	selectEditors,
	selectSelectedEditors,
	(editors, selectedEditors) => {
		return editors.filter(u => u?.compositeId && selectedEditors[u.compositeId]);
	}
)

// ***********
// LISTS - LINE NAMES
// ***********

export const selectLineNames = createSelector(
	selectItems, 
	(items) => {
		const lineNames = new Set([]);
		items.forEach(item => {
			if (item?.line?.name) {
				lineNames.add(item.line.name);
			}
		});
		return [...lineNames].sort();
	}
);

export const selectSelectedLineNamesList = createSelector(
	selectLineNames,
	selectSelectedLineNames,
	(lineNames, selectedLineNames) => {
		return lineNames.filter(name => selectedLineNames[name]);
	}
)

// ***********
// LISTS - SURVEY TYPES
// ***********

export const selectSurveyTypes = createSelector(
	selectItems, 
	(items) => {
		const surveyTypes = new Set([]);
		items.forEach(item => {
			if (item?.survey?.type) {
				surveyTypes.add(item.survey.type);
			}
		});
		return [...surveyTypes].sort();
	}
);

export const selectSelectedSurveyTypesList = createSelector(
	selectSurveyTypes,
	selectSelectedSurveyTypes,
	(surveyTypes, selectedSurveyTypes) => {
		return surveyTypes.filter(type => selectedSurveyTypes[type]);
	}
)


// ***********
// LISTS - ITEMS
// ***********

export const selectFilteredItemsList = createSelector(
	selectItems,
	selectSelectedCustomers,
	selectSelectedOwners,
	selectSelectedEditors,
	selectSelectedLineNames,
	selectSelectedSurveyTypes,
	(items, customersMap, ownersMap, editorsMap, lineNamesMap, surveyTypesMap) => {
		const hasSelectedCustomers = customersMap && Object.keys(customersMap).length > 0;
		const hasSelectedOwners = ownersMap && Object.keys(ownersMap).length > 0;
		const hasSelectedEditors = editorsMap && Object.keys(editorsMap).length > 0;
		const hasSelectedLineNames = lineNamesMap && Object.keys(lineNamesMap).length > 0;
		const hasSelectedSurveyTypes = surveyTypesMap && Object.keys(surveyTypesMap).length > 0;

		if (!hasSelectedCustomers && !hasSelectedOwners && !hasSelectedEditors && !hasSelectedLineNames && !hasSelectedSurveyTypes) {
			return items;
		}

		const hasCustomer = (item) => {
			if (!hasSelectedCustomers) {
				return true;
			}
			return customersMap[item?.customer?.name];
		};

		const hasOwner = (item) => {
			if (!hasSelectedOwners) {
				return true;
			}
			return ownersMap[item?.owner?.compositeId];
		};

		const hasEditor = (item) => {
			if (!hasSelectedEditors) {
				return true;
			}
			return editorsMap[item?.editedBy?.compositeId];
		};

		const hasLineName = (item) => {
			if (!hasSelectedLineNames) {
				return true;
			}
			return lineNamesMap[item?.line?.name];
		};

		const hasSurveyType = (item) => {
			if (!hasSelectedSurveyTypes) {
				return true;
			}
			return surveyTypesMap[item?.survey?.type];
		};

		return items.filter(item => {
			return hasCustomer(item) && hasOwner(item) && hasEditor(item) && hasLineName(item) && hasSurveyType(item);
		});
	}
);

export const selectFilteredServerItemsList = createSelector(
	selectServerItems,
	selectSelectedCustomers,
	selectSelectedOwners,
	selectSelectedEditors,
	selectSelectedLineNames,
	selectSelectedSurveyTypes,
	(items, customersMap, ownersMap, editorsMap, lineNamesMap, surveyTypesMap) => {
		const hasSelectedCustomers = customersMap && Object.keys(customersMap).length > 0;
		const hasSelectedOwners = ownersMap && Object.keys(ownersMap).length > 0;
		const hasSelectedEditors = editorsMap && Object.keys(editorsMap).length > 0;
		const hasSelectedLineNames = lineNamesMap && Object.keys(lineNamesMap).length > 0;
		const hasSelectedSurveyTypes = surveyTypesMap && Object.keys(surveyTypesMap).length > 0;

		if (!hasSelectedCustomers && !hasSelectedOwners && !hasSelectedEditors && !hasSelectedLineNames && !hasSelectedSurveyTypes) {
			return items;
		}

		const hasCustomer = (item) => {
			if (!hasSelectedCustomers) {
				return true;
			}
			return customersMap[item?.customer_name];
		};

		const hasOwner = (item) => {
			if (!hasSelectedOwners) {
				return true;
			}
			return ownersMap[computeUserCompositeId(item?.userid, item?.user_full_name)];
		};

		const hasEditor = (item) => {
			if (!hasSelectedEditors) {
				return true;
			}
			return editorsMap[computeUserCompositeId(item?.created_by_userid, item?.created_by_user_full_name)];
		};

		const hasLineName = (item) => {
			if (!hasSelectedLineNames) {
				return true;
			}
			return lineNamesMap[item?.line_name];
		};

		const hasSurveyType = (item) => {
			if (!hasSelectedSurveyTypes) {
				return true;
			}
			return surveyTypesMap[item?.survey_type];
		};

		return items.filter(item => {
			return hasCustomer(item) && hasOwner(item) && hasEditor(item) && hasLineName(item) && hasSurveyType(item);
		});
	}
);

